import React from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import {
  DASHBOARD_APP_BASE,
  SUPPORTED_DASHBOARDS,
} from "src/layouts/HomePage/Config/DashboardRoutes";
import { NotificationProvider } from "../contexts/NotificationContext";
import { MenuProvider } from "../contexts/MenuContext";
import SignUp from "../layouts/Authentication/SignUp";
import Otp from "../layouts/Authentication/Otp";
import EmailSent from "../layouts/Authentication/ResetPasswordNotification";
import LoginIn from "../layouts/Authentication/LogIn";
import ResetPasswordEmail from "../layouts/Authentication/ForgotPasswordEmail";
import MakeNewPassword from "../layouts/Authentication/MakeNewPassword";
import Sidebar from "../layouts/OnboardingSidebar/OnboardingSidebar";
import MagicScreen from "../layouts/MagicScreen";
import PrivacyPolicy from "../layouts/PrivacyPolicy/PrivacyPolicy";
import HealthCheckPage from "../components/miscellaneous/HealthCheck";
import Chatbot from "../layouts/AskDatatram/ChatLayout";
import Dashboard from "../layouts/HomePage/HomepageLayout";
import SourceConnector from "../layouts/SourceConnector/SourceConnectorPage";
import UserManagement from "../layouts/UserManagement/UserManagement";
import AccountSettings from "../layouts/MyAccount/Settings/AccountSettings";
import NotificationPreferences from "../layouts/MyAccount/Notifications/NotificationPreferences";
import PaymentPlans from "../layouts/PaymentPlans";
import AllNotifications from "../layouts/MyAccount/Notifications/AllNotifications";
import Overview from "../layouts/Engagements/Overview/Overview";
import CreateCampaign from "../layouts/Engagements/Campaigns/CreateCampaign";
import CampaignAnalytics from "../layouts/Engagements/Campaigns/CampaignDashboards";
import CampaignPerformance from "../layouts/Engagements/Campaigns/CampaignPerformance";
import CompareCampaigns from "../layouts/Engagements/Campaigns/CompareCampaigns";
import Audience from "../layouts/Engagements/Audience/AudiencePage/AudienceOverview";
import ListORSegment from "../layouts/Engagements/Audience/CreateAudience/ListORSegmentSelecter";
import CreateSegment from "../layouts/Engagements/Audience/CreateAudience/CreateSegment";
import CreateList from "../layouts/Engagements/Audience/CreateAudience/CreateList";
import ModifyFile from "../layouts/Engagements/Audience/CreateAudience/handleCSV/ModifyFile";
import DashboardPage from "../components/TableauDashboard/RenderDashboards";
import UserIntro from "../layouts/ProfileSetup/UserIntro";
import SessionChecker from "src/components/common/SessionChecker";
import FetchCategories from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/AIEmailEditor";
import CampaignEditorRoute from "src/layouts/Engagements/Campaigns/shared/CampaignEditorRoute";
import TemplateCampaignWizard from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/Template/TemplateCampaignWizard";
import WhatsAppCampaignWizard from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/WhatsAppCampaignWizard";
import CampaignReviewAndSend from "src/layouts/Engagements/Campaigns/CampaignReviewAndSend";
import ProtectedRoute from "./ProtectedRoute";
import RequireDashboardAuth from "./RequireDashboardAuth";
import RequireSectionAccess from "./RequireSectionAccess";
import {
  hasSocialListeningAccess,
  hasUserManagementAccess,
} from "src/utils/sectionAccess";
import AIReviewAndSend  from "src/layouts/AskDatatram/AIReviewAndSend";
import SocialListing from "../layouts/SocialListingAgent/SocialListingAgent";
import BrandAnalysis from "../layouts/SocialListingAgent/Pages/BrandAnalysisForm";
import CompetitiveAnalysisForm from "../layouts/SocialListingAgent/Pages/CompetitiveAnalysisForm";
import BrandListeningReport from "../layouts/SocialListingAgent/Pages/BrandListeningReport";
import SocialListingLoader from "../layouts/SocialListingAgent/Pages/Loader";
import SocialListeningHistory from "../layouts/SocialListingAgent/Pages/History";
import AIChatScreen from "src/layouts/AskDatatram/ChatScreen";
import { PaidMediaDashboard } from "src/dashboards/paidMedia";
import HelpSupportPage from "src/layouts/HelpSupport/HelpSupportPage";

/** Old `/account/dashboards/...` → `/dashboard/...` */
const LegacyAccountDashboardsFolderRedirect: React.FC = () => {
  const location = useLocation();
  const suffix = location.pathname.replace(/^\/account\/dashboards\/?/, "");
  const to = suffix
    ? `${DASHBOARD_APP_BASE}/${suffix.replace(/^\/+/, "")}`
    : DASHBOARD_APP_BASE;
  return <Navigate to={to} replace state={location.state} />;
};

const UserRoutes: React.FC = () => (
  <NotificationProvider>
    <MenuProvider>
      <SessionChecker />
      <Routes>
        {/* ======== Routes for Datatram's Authentication =======*/}
        <Route path="/" element={<Navigate to="/log-in" />} />
        <Route path="/sign-up" element={<SignUp />} />
        <Route path="/verify-otp"   element={<ProtectedRoute allowedFrom="sign-up" redirectTo="/log-in"><Otp /></ProtectedRoute>} />
        <Route path="/log-in" element={<LoginIn />} />
        {/* ======== Routes for Reset/Forget Passwords =======*/}
        <Route path="/forgot-password/confirmation"   element={<ProtectedRoute allowedFrom="forgot-password" redirectTo="/log-in"><EmailSent /></ProtectedRoute>} />
        <Route path="/forgot-password" element={<ResetPasswordEmail />} />
        <Route path="/reset-password"   element={<MakeNewPassword />} />
        {/* ======= Routes for Static / Miscellaneous Pages ======*/}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/health" element={<HealthCheckPage />} />
        <Route path="/ask-datatram" element={<Chatbot />} />
        <Route
          path="/paid-media"
          element={<Navigate to="/dashboard/paid-media-dashboard" replace />}
        />
        <Route
          path="/paid-media/meta-ads-report"
          element={
            <Navigate
              to="/dashboard/paid-media-dashboard/meta-ads-report"
              replace
            />
          }
        />
        <Route
          path="/account/dashboards/product-analytics-dashboard"
          element={
            <Navigate to="/dashboard/sales-product-analytics-dashboard" replace />
          }
        />
        <Route
          path="/account/dashboards/marketing-analytics-dashboard"
          element={<Navigate to="/dashboard/web-analytics-dashboard" replace />}
        />
        <Route
          path="/account/dashboards/meta-ads-overview"
          element={<Navigate to="/dashboard/paid-media-dashboard" replace />}
        />
        <Route
          path="/account/dashboards"
          element={<Navigate to={DASHBOARD_APP_BASE} replace />}
        />
        <Route
          path="/account/dashboards/*"
          element={<LegacyAccountDashboardsFolderRedirect />}
        />
        <Route
          path="/account/paid-media-dashboard/meta-ads-report"
          element={
            <Navigate
              to="/dashboard/paid-media-dashboard/meta-ads-report"
              replace
            />
          }
        />
        {SUPPORTED_DASHBOARDS.map(d => {
          const slug = d.route.replace(
            new RegExp(`^${DASHBOARD_APP_BASE}/`),
            "",
          );
          return (
            <Route
              key={d.dashboardId}
              path={`/account/${slug}`}
              element={<Navigate to={d.route} replace />}
            />
          );
        })}
        {/* ======== Routes for Magic Screen =======*/}
        <Route
          path="/welcome-to-datatram/data-loading"
          element={<MagicScreen />}
        />
        {/* ======== Routes for Onboarding Screens =======*/}
        <Route path="/welcome-to-datatram" element={<Sidebar />}>
 <Route path="basic-information"     element={ <ProtectedRoute allowedFrom="welcome" redirectTo="/log-in"> <UserIntro /></ProtectedRoute> }/>   
    <Route path="connect-sources"     element={<ProtectedRoute allowedFrom="basic-information" redirectTo="/log-in"> <SourceConnector /></ProtectedRoute>} />  

        {/* <Route path="connect-sources" /> */}
        </Route>

        <Route path="/dashboard" element={<Dashboard />}>
          <Route
            path="paid-media-dashboard/meta-ads-report"
            element={
              <RequireDashboardAuth>
                <PaidMediaDashboard />
              </RequireDashboardAuth>
            }
          />
          <Route
            index
            element={
              <RequireDashboardAuth>
                <DashboardPage />
              </RequireDashboardAuth>
            }
          />
          <Route
            path=":dashboardSlug"
            element={
              <RequireDashboardAuth>
                <DashboardPage />
              </RequireDashboardAuth>
            }
          />
        </Route>

        <Route path="/account" element={<Dashboard />}>
          <Route index element={<Navigate to={DASHBOARD_APP_BASE} replace />} />
          <Route
            path="/account/connect-sources"
            element={<SourceConnector />}
          />
          <Route
            path="user-management"
            element={
              <RequireSectionAccess hasAccess={hasUserManagementAccess}>
                <UserManagement />
              </RequireSectionAccess>
            }
          />
          <Route path="help-and-support" element={<HelpSupportPage />} />
          <Route
            element={
              <RequireSectionAccess hasAccess={hasSocialListeningAccess}>
                <Outlet />
              </RequireSectionAccess>
            }
          >
            <Route path="social-listing-agent" element={<SocialListing  />} />
            <Route path="social-listing-agent/brand-analysis" element={<BrandAnalysis />} />
            <Route path="/account/social-listing-agent/competitive-analysis" element={<CompetitiveAnalysisForm />} />
            <Route path="/account/social-listing-agent/loader" element={<SocialListingLoader />} />
            <Route path="/account/social-listing-agent/:runId" element={<BrandListeningReport />} />
            <Route path="/account/social-listing-agent/history" element={<SocialListeningHistory />} />
          </Route>

          <Route path="sources" element={<SourceConnector />} />
          <Route path="account-setting" element={<AccountSettings />} />

          {/* My Account */}
          <Route
            path="my-account/all-notifications"
            element={<AllNotifications />}
          />
          <Route
            path="my-account/account-settings"
            element={<AccountSettings />}
          />
          <Route path="my-account/payment-plan" element={<PaymentPlans />} />
          <Route
            path="my-account/notification-preferences"
            element={<NotificationPreferences />}
          />

          {/* Engagement */}
          <Route
            path="engagement/email-analytics-overview"
            element={<Overview />}
          />
          <Route
            path="engagement/ai-email-editor"
            element={<FetchCategories />}
          />
          <Route
            path="engagement/email-template-editor"
            element={<CampaignEditorRoute Wizard={TemplateCampaignWizard} />}
          />
          <Route
            path="engagement/whatsapp-campaign-editor"
            element={<CampaignEditorRoute Wizard={WhatsAppCampaignWizard} />}
          />
        <Route path="review-and-send" element={<CampaignReviewAndSend />} />
        <Route path="ai-chat" element={<AIChatScreen />} />
        <Route path="ai-review-and-send" element={<AIReviewAndSend />} />

          <Route
            path="engagement/create-campaign"
            element={<CreateCampaign />}
          />
          <Route
            path="engagement/campaign-analytics"
            element={<CampaignAnalytics />}
          />
          <Route
            path="engagement/campaign-performance"
            element={<CampaignPerformance />}
          />
          <Route
            path="engagement/compare-campaigns"
            element={<CompareCampaigns />}
          />
          <Route path="engagement/modify-file" element={<ModifyFile />} />
          <Route path="engagement/audience" element={<Audience />} />
          <Route path="engagement/create-segment" element={<CreateSegment />} />
          <Route path="engagement/create-list" element={<CreateList />} />
          <Route
            path="engagement/select-list-or-segment"
            element={<ListORSegment />}
          />
        </Route>
      </Routes>
    </MenuProvider>
  </NotificationProvider>
);

export default UserRoutes;
